// MainLoader — shell override of the brand loader, for white-label hosts
// (WHITELABEL-BRANDED-BOOT-1, tickets/opus.md).
//
// Nika, via relman 2026-08-19: "Symbols loader first, then Bellforge
// branding" (accepted — no edge injection, no per-host index.html), and the
// second stage should be "a rotating Bellforge SVG logo", NOT the spiral.
//
// So the sequence a white-label visitor sees is deliberately two-stage:
//   1. the inline #boot-spiral in index.html — Symbols, pre-bundle, before
//      any JS can know whose host this is. Unchanged.
//   2. THIS loader, once the bundle is live: the org's own mark, rotating,
//      on the org's own ground.
//
// ── Additive by construction ─────────────────────────────────────────────
// Every branded prop is a factory gated on `s.root.whitelabelBrand`, which
// is null on every platform host and for any custom domain without a record
// (functions/whitelabelBrand.js). Unbranded, this component contributes
// nothing but a hidden empty child — the brand MainLoader renders exactly as
// it does today. The self-name `extends` is the documented library-override
// pattern, already proven in this package by components/AppSidebar.js.
//
// ── Why the Symbols marks are HIDDEN rather than replaced ────────────────
// `LogoLoader.html` / `LoaderRatio.Spinner.html` are static strings on the
// brand components. Overriding them with a factory means owning the
// unbranded value too — a factory that returns null on the platform path
// would blank the Symbols mark for everyone (and a null return is a no-op on
// an UPDATE render, so it would fail asymmetrically). Hiding the two Symbols
// marks and rendering the brand mark as a SIBLING keeps the platform path
// byte-identical and needs no copy of the brand's own markup.
//
// ── `hide` AND an explicit `display` ─────────────────────────────────────
// Both gates on purpose, same as ContextDropdown's Avatar slot: `if:` is
// inert in this v3.14 build, and published element builds up to 3.14.779
// carry FW-HIDE-UNAPPLIED-1 (a `hide` factory that is true from its first
// run is clobbered by the explicit-display pass; fixed in smbls 7031e3d60,
// unpublished while the npm publish chain is billing-locked). The explicit
// display is the pass that wins on every runtime, old and fixed.

import { whitelabelGround } from '@symbo-ls/workspace-shared/components/WhitelabelSignin.js'

// The brand bag for the live root, or null. Declared per-consumer rather
// than imported: these are prop factories, and a bare module-scope helper
// is the frank trap this repo documents repeatedly.
export const MainLoader = {
  extends: 'MainLoader',

  // The org's ground, resolved by the SAME rule the branded sign-in uses —
  // one scheme decision for the whole white-label surface, LIGHT BY DEFAULT
  // (Nika 2026-08-19: "make both versions - light by default"). Importing the
  // helper rather than re-deriving it is deliberate: two copies of a
  // precedence rule drift, and this one already has a subtle case (an
  // explicit 'auto' must follow the OS, not the brand).
  background: (el, s) => {
    const brand = s.root && s.root.whitelabelBrand
    return brand ? whitelabelGround(s) : null
  },

  LoaderRatio: {
    Spinner: {
      // The Symbols swirl IS the Symbols identity — it has no place on a
      // customer's own domain. This is the "not the spiral" half of the ask.
      hide: (el, s) => !!(s.root && s.root.whitelabelBrand),
      // TWO REAL BRANCHES. This was `: null`, and here that was a genuine
      // TOMBSTONE: the base declares `display: 'flex'` with
      // `align: 'center center'`
      // (company/packages/brand/components/LoaderRatio.js:67), so on the
      // ORDINARY non-white-label path — every platform user, at boot — the
      // null erased it and dropped the spiral to `block`, losing its
      // centring. `'flex'` restates the base value rather than inventing one.
      display: (el, s) => (s.root && s.root.whitelabelBrand ? 'none' : 'flex')
    }
  },

  LogoLoader: {
    hide: (el, s) => !!(s.root && s.root.whitelabelBrand),
    // DELIBERATELY still `null`, and allowlisted in the tombstone lock.
    // Checked rather than assumed: the brand's LogoLoader
    // (company/packages/brand/components/LogoLoader.js) declares NO `display`
    // at all, and neither does the MainLoader slot it sits in — it is
    // positioned with `position`/`top`/`left`/`transform`. With nothing
    // inherited to erase, this null deletes nothing. Giving it a real branch
    // would ADD a display this node has never had, which is the riskier edit.
    display: (el, s) => (s.root && s.root.whitelabelBrand ? 'none' : null)
  },

  // The branded mark. Sits in the same centred slot the LogoLoader occupies
  // (the brand MainLoader positions LogoLoader absolutely at 50%/50%; this
  // mirrors it rather than depending on it).
  //
  // ── TWO MOTIONS, CHOSEN BY THE RECORD ────────────────────────────────────
  // Nika 2026-09-10: "change loading spinner only for vault - keep bellforge
  // and gita as was". So the motion is a per-tenant RECORD decision, not a
  // shell decision: `branding.loaderMotion === 'dial'` opts an org into the
  // combination-dial lockup; every other value, and the absent key, keep the
  // `flipY` coin flip this loader has shipped since 2026-08-20. The key is
  // read off the SAME public brand object the mark itself comes from, so a
  // tenant changes its own boot motion by changing its own record — no
  // deploy, no per-host branch in the shell.
  //
  // ── The lockup: HOST → RING + DIAL ───────────────────────────────────────
  // Three elements, one for each job, because one element cannot hold two
  // transforms:
  //   BrandMark (this host) — the box, margin-centred. In dial mode it owns
  //                           the ONE-SHOT arrival (scale-in + ink fade); in
  //                           flip mode it owns the flip itself. Owns the ink.
  //   DialRing              — the progress ring. DIAL MODE ONLY.
  //   Dial                  — the 56px inner wrapper that TURNS in dial mode,
  //                           inert in flip mode, and the only element that
  //                           carries the org's markup in BOTH.
  // The turn is on `Dial`, never on this host: keyframe transforms REPLACE
  // the base transform, and the host's own arrival keyframes already own the
  // property in dial mode. In flip mode the wrapper is a plain 56px box
  // inside the 56px host and changes nothing — the flip still runs on the
  // host exactly as it always did, and the mark rides it.
  // One shape for both modes on purpose: two DOM shapes behind a flag drift,
  // and the flip is the path 100% of today's tenants take.
  BrandMark: {
    hide: (el, s) => !(s.root && s.root.whitelabelBrand),
    display: (el, s) => (s.root && s.root.whitelabelBrand ? 'flex' : 'none'),
    // ⚠️ ABOVE THE RATIO PANEL, not above the LogoLoader. Measured live
    // 2026-09-10 on the shell: brand's `LoaderRatio` is itself a FIXED
    // full-screen panel at `zIndex: 9990099` whose `:after` paints a
    // rgba(5,5,5,.98) vignette over the centre of the screen — and the shell's
    // MainLoader root computes `position: static`, so it opens no stacking
    // context of its own and the two are ordered against each other directly.
    // At the inherited `zIndex: 2` the branded mark rendered UNDERNEATH that
    // 98%-opaque vignette: present in the DOM, hit-testable, invisible on
    // screen. One step above the panel is all it takes, and nothing else is on
    // screen at boot to collide with.
    zIndex: 9990100,
    position: 'absolute',
    top: '50%',
    left: '50%',
    // Margin centering, NOT transform — the animations own the `transform`
    // property outright (keyframe transforms REPLACE the base transform, they
    // do not compose), so a translate3d here would be dropped for the whole
    // animation and the mark would be anchored at its top-left corner, half a
    // mark off-centre. The box is knowable, so the offsets are knowable too:
    // the margin is a FACTORY that reads the box back (`el.boxSize`) and
    // returns its negative half on the two start edges, so the size and the
    // centring are ONE number — resize the box here and the mark stays
    // centred. Same technique the boot screen's .boot-logo uses in index.html.
    // History: before smbls 3.14.800 a `margin` FACTORY emitted nothing (a
    // multi-part spacing result is an array the reactive style effect could
    // not apply — FW-MARGIN-FACTORY-PROP-DROPPED-SILENTLY-1). Measured live
    // 2026-09-10 on this shell: the computed margin was `0px` and the mark sat
    // exactly half a box down and right of centre, so the pair was typed twice
    // as literals. This shell locks smbls 3.14.807, which carries the fix, and
    // __tests__/whitelabelMainLoaderCentring.test.js reads the resolved margin
    // back through the real engine — an engine that drops it again fails there
    // instead of shipping an off-centre mark.
    // The box is ONE size for both motions: 88px, with the 56px mark centred
    // in it by flex. In flip mode the extra air is invisible (the host paints
    // nothing) and the mark flips about the same centre, at the same size, as
    // it always has — only the box around it is bigger.
    // ⚠️ WHY 88 AND NOT 80: the mark TURNS. A tenant whose ink reaches the
    // corners of its own viewBox sweeps a circle of 56·√2 ≈ 79.2px, i.e. 39.6px
    // from the centre, so a ring whose inner edge sat at 37px would be crossed
    // by that mark twice per turn. At 88px the inner edge is 41px and NO mark,
    // whatever its shape, can ever touch the ring.
    boxSize: '88px',
    margin: (el, s) => {
      const half = parseFloat(el.boxSize) / 2
      return `${-half}px 0 0 ${-half}px`
    },
    align: 'center center',
    // `accent` tints BOTH the mark and the ring through `currentColor`, which
    // is how both brand loader marks already take their colour.
    color: (el, s) => {
      const brand = s.root && s.root.whitelabelBrand
      return (brand && brand.accent) || 'title'
    },
    // DIAL MODE: the arrival, once — scale-in from 0.85 with the ink fading up
    // over one timing step (`A` = 150ms). Deliberately NOT part of the turn
    // cycle; see the `dialEnter` note in designSystem/animation.js.
    // FLIP MODE (the default): the unchanged 3D coin flip — `flipY`, 2.4s,
    // linear, forever, on this host. Byte-for-byte the motion bellforge and
    // gita have today.
    animation: (el, s) => {
      const brand = s.root && s.root.whitelabelBrand
      return brand && brand.loaderMotion === 'dial' ? 'dialEnter' : 'flipY'
    },
    animationDuration: (el, s) => {
      const brand = s.root && s.root.whitelabelBrand
      return brand && brand.loaderMotion === 'dial' ? 'A' : '2.4s'
    },
    animationTimingFunction: (el, s) => {
      const brand = s.root && s.root.whitelabelBrand
      return brand && brand.loaderMotion === 'dial' ? 'defaultBezier' : 'linear'
    },
    animationIterationCount: (el, s) => {
      const brand = s.root && s.root.whitelabelBrand
      return brand && brand.loaderMotion === 'dial' ? 1 : 'infinite'
    },
    transformOrigin: '50% 50%',
    aria: { hidden: 'true' },

    // The ring. Not an `Icon` and not a border: it has to FILL clockwise, and
    // the only property that draws a partial circle without a gradient, a
    // mask or a second painted layer is `stroke-dashoffset` on a stroked
    // circle. Rule 62 is about ICONS (sprite-resolved, theme-swapped, reused);
    // this is a one-off decorative meter that belongs to this loader alone and
    // could not resolve through the shell's icon set anyway. Same declarative
    // svg form the /infra edge layer already uses in this package.
    // Geometry is viewBox math: an 88-unit box, r=42 leaves the 2-unit stroke
    // inside the box, circumference 2·π·42 ≈ 264 = the dash period.
    // `rotate(-90 44 44)` starts the fill at twelve o'clock. It is an SVG
    // attribute, not a CSS transform, so it can never collide with the
    // keyframes.
    DialRing: {
      // DIAL MODE ONLY. Same belt-and-braces gate the Symbols marks above use:
      // `hide` for the fixed runtimes, an explicit `display` for the published
      // ones (FW-HIDE-UNAPPLIED-1).
      hide: (el, s) => {
        const brand = s.root && s.root.whitelabelBrand
        return !(brand && brand.loaderMotion === 'dial')
      },
      display: (el, s) => {
        const brand = s.root && s.root.whitelabelBrand
        return brand && brand.loaderMotion === 'dial' ? 'block' : 'none'
      },
      tag: 'svg',
      position: 'absolute',
      top: '0',
      left: '0',
      boxSize: '100% 100%',
      // Width/height as INLINE ATTRS as well: the same published-build trap
      // the mark below documents — if the emitted class is ever dropped, the
      // svg still lays out at its intrinsic 88×88 instead of 0×0.
      attr: {
        width: '88',
        height: '88',
        viewBox: '0 0 88 88',
        fill: 'none'
      },
      RingArc: {
        tag: 'circle',
        attr: {
          cx: '44',
          cy: '44',
          r: '42',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '2',
          // The accent at ~35% — `stroke-opacity` rather than a colour token
          // because the ink here is `currentColor` (a per-tenant record value,
          // not a design-system colour), and only the stroke should soften.
          'stroke-opacity': '0.35',
          'stroke-linecap': 'round',
          'stroke-dasharray': '264',
          // The RESTING state, and the reduced-motion state: a whole ring.
          'stroke-dashoffset': '0',
          transform: 'rotate(-90 44 44)'
        },
        animation: 'dialRingFill',
        animationDuration: 'K1',
        animationTimingFunction: 'defaultBezier',
        animationIterationCount: 'infinite',
        // ⚠️ Load-bearing. css-in-props defaults a non-neutral last keyframe
        // to `both`; this animation ends transparent, so the default would
        // leave a reduced-motion visitor staring at an invisible ring. `none`
        // hands the resting state back to the attributes above.
        animationFillMode: 'none'
      }
    },

    // The dial itself: the org's mark. It TURNS in dial mode and is a plain
    // inert 56px box in flip mode (where the host does the flipping and this
    // wrapper simply rides it). The size is applied to THIS element and the
    // svg is sized by INLINE ATTRS injected into the markup below — NOT via
    // an `& svg` selector block. Measured 2026-08-19: such blocks are dropped
    // by published css-in-props builds (the trap LogoLoader.js documents), so
    // an earlier version rendered a 0x0, un-animated mark — i.e. the branded
    // loader showed NOTHING once the Symbols spiral was hidden.
    // The global prefers-reduced-motion rule in brand/designSystem/reset.js
    // collapses the turn for users who ask; the mark then rests at 0°.
    Dial: {
      display: 'flex',
      align: 'center center',
      boxSize: '56px',
      animation: (el, s) => {
        const brand = s.root && s.root.whitelabelBrand
        return brand && brand.loaderMotion === 'dial' ? 'dialTurn' : 'none'
      },
      animationDuration: 'K1',
      animationTimingFunction: 'defaultBezier',
      animationIterationCount: 'infinite',
      transformOrigin: '50% 50%',
      // Raw record markup: a published white-label logo is an inline SVG
      // string on the org record, sanitized server-side before it is ever
      // projected into the public payload. It cannot be an `Icon` — a sprite
      // reference resolves against the SHELL's icon set, which by definition
      // does not carry a customer's mark.
      html: (el, s) => {
        const brand = s.root && s.root.whitelabelBrand
        if (!brand) return ''
        const markup = brand.loaderLogoSvg || brand.logoSvg || ''
        if (typeof markup !== 'string' || !markup) return ''
        if (!/^\s*<svg\b/i.test(markup)) return markup
        // The stored markup carries only a `viewBox` — without explicit
        // width/height it lays out at 0x0.
        return markup
          .replace(/\s(width|height)\s*=\s*(".*?"|'.*?')/gi, '')
          .replace(/^\s*<svg\b/i, '<svg width="56" height="56" preserveAspectRatio="xMidYMid meet"')
      }
    }
  }
}
